import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, useAuth, axiosInstance } from '../App';
import { useNotifications } from '../contexts/NotificationContext';
import { getToken } from '../utils/auth';
import { 
  ArrowLeft, MapPin, Phone, Mail, Globe, Clock, Star, Heart,
  MessageCircle, Users, Utensils, Accessibility, ParkingCircle, Baby, Send, Loader2, Flag, ShieldCheck, Bell, BellRing, Smartphone, Share, PlusSquare, Percent, Calendar, Search, CalendarOff, CalendarClock, Share2, Instagram, Facebook, Youtube, Navigation
} from 'lucide-react';
import ReportModal from '../components/ReportModal';
import FoundingBusinessBadge from '../components/FoundingBusinessBadge';
import ShareButtons from '../components/ShareButtons';
import ShareInlineButton from '../components/ShareInlineButton';
import EventShareControl from '../components/EventShareControl';
import ClaimForm from '../components/ClaimForm';
import SeededListingNotice from '../components/SeededListingNotice';
import RemovalRequestModal from '../components/RemovalRequestModal';
import AddToCalendarButton from '../components/AddToCalendarButton';
import OwnerInlineControls from '../components/OwnerInlineControls';
import { formatTimeAgo } from '../utils/timeAgo';
import { toast } from 'sonner';
import { tf } from '../utils/translateFeature';
import { formatPlaceName } from '../utils/placeName';
import { formatPhone } from '../utils/phoneFormat';
import BusinessMap from '../components/BusinessMap';
import { readDraftPreview, clearDraftPreview } from '../utils/draftPreview';
import { trackBusinessView } from '../components/FollowPromptBanner';

/** Mask a phone number, showing only the first ~40% of digits. e.g. "+34 950 123 456" → "+34 95• ••• •••" */
const maskPhone = (phone) => {
  if (!phone) return '••• ••• •••';
  const raw = phone.replace(/[^\d+]/g, '');
  const cutoff = Math.min(6, Math.ceil(raw.length * 0.4));
  let result = '';
  let digitIndex = 0;
  for (const ch of phone) {
    if (/[\d+]/.test(ch)) {
      result += digitIndex < cutoff ? ch : '\u2022';
      digitIndex++;
    } else {
      result += ch; // preserve original spacing
    }
  }
  return result;
};

const CATEGORY_I18N_MAP = {
  'Food & Dining': 'categories.food', 'Music & Nightlife': 'categories.music',
  'Family Activities': 'categories.family', 'Special Offers': 'categories.offers',
  'Shopping/Retail': 'categoryShopping', 'Health & Wellness': 'categoryHealthWellness',
  'Beauty & Spa': 'categoryBeautySpa', 'Sports & Fitness': 'categorySports',
  'Arts & Culture': 'categoryArts', 'Events & Entertainment': 'categoryEvents',
  'Education': 'categoryEducation', 'Professional Services': 'categoryServices',
  'Automotive': 'categoryAutomotive', 'Home Services': 'categoryHomeServices',
  'Travel & Tourism': 'categoryTravel', 'Technology': 'categoryTechnology',
  'Vets': 'categoryVets',
  'Volunteer Work': 'categoryVolunteerWork', 'Jobs': 'categoryJobs',
  'Community Help': 'categoryCommunityHelp', 'Other': 'categoryOther',
};

const BusinessDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const isDraft = isPreview && searchParams.get('draft') === '1';
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const notifications = useNotifications();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Iter 159 — Detect a malformed /:id segment up front so we never even fire
  // an API call that's guaranteed to 404. This also drives the friendlier
  // "broken link" copy rendered below.
  const idStr = id ? String(id).trim().toLowerCase() : '';
  const isInvalidId = !idStr || ['undefined', 'null', 'false', 'nan'].includes(idStr);

  const [reportModal, setReportModal] = useState({ open: false, targetType: '', targetId: '', targetName: '' });
  const [reviewData, setReviewData] = useState({ reviews: [], averageRating: 0, totalCount: 0 });
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: '' });
  const [reviewError, setReviewError] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [claimStatus, setClaimStatus] = useState(null);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimMessage, setClaimMessage] = useState('');
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimSuccess, setClaimSuccess] = useState(false);
  // Iter 128 — Removal/edit request modal state
  const [removalModalOpen, setRemovalModalOpen] = useState(false);
  const [removalDefaultType, setRemovalDefaultType] = useState('removal');
  const [claimError, setClaimError] = useState('');
  const [followToast, setFollowToast] = useState(false);
  // Iter 178 — highlight target for `#offer-...` / `#event-...` deep-links.
  const [highlightedItemId, setHighlightedItemId] = useState(null);
  // Iter 179 — flag indicating both offers & events have completed loading
  // (covers fetch success AND fetch error) so we don't fire the missing
  // deep-link banner during the initial render.
  const [offersEventsLoaded, setOffersEventsLoaded] = useState(false);
  // Iter 179 — true when the URL hash points to an offer/event that
  // doesn't exist on this business after data has loaded.
  const [deepLinkMissing, setDeepLinkMissing] = useState(false);
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);
  const [businessOffers, setBusinessOffers] = useState([]);
  const [businessEvents, setBusinessEvents] = useState([]);
  const [isFavourited, setIsFavourited] = useState(false);

  // Session ID for analytics
  const sessionId = React.useMemo(() => {
    let sid = sessionStorage.getItem('f2d_sid');
    if (!sid) { sid = crypto.randomUUID(); sessionStorage.setItem('f2d_sid', sid); }
    return sid;
  }, []);

  useEffect(() => {
    fetchBusiness();
    // Skip server calls in draft preview mode (no real reviews/offers/events tied to a draft)
    if (!isDraft) {
      fetchReviews();
      fetchBusinessOffers();
      fetchBusinessEvents();
    }
  }, [id, isDraft]);

  // Check claim status for unclaimed listings
  useEffect(() => {
    if (user && business && business.source === 'seed' && !business.isClaimed) {
      axiosInstance.get(`/claims/mine/${id}`).then(async (res) => {
        const claim = res.data;
        const status = claim?.status || 'none';
        setClaimStatus(status);
        // If claim was approved, refresh session to get business role
        if (status === 'approved' && user.role !== 'business') {
          try {
            const token = getToken();
            const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
            const refreshRes = await axios.get(`${API}/auth/refresh-session`, { headers: { Authorization: `Bearer ${token}` } });
            storage.setItem('token', refreshRes.data.access_token);
            storage.setItem('role', refreshRes.data.role);
            storage.setItem('userId', refreshRes.data.user_id);
            // Reload to apply new role across the app
            window.location.reload();
          } catch (err) {
            console.error('Session refresh failed:', err);
          }
        }
      }).catch(() => setClaimStatus('none'));
    }
  }, [user, business, id]);

  // Track view on load
  useEffect(() => {
    if (id && !isPreview) {
      trackBusinessView();
      axios.post(`${API}/listings/${id}/view`, { sessionId }).catch(() => {});
    }
  }, [id, sessionId, isPreview]);

  // Check if this business is favourited — logged-in users hit the account
  // endpoint, anonymous users mirror their device subscription's favourites.
  useEffect(() => {
    if (!id) return;
    if (user) {
      const token = getToken();
      if (!token) return;
      axios.get(`${API}/user/favorites`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setIsFavourited((res.data || []).some(b => b.id === id)))
        .catch(() => {});
    } else {
      const favs = notifications?.preferences?.favouriteBusinessIds || [];
      setIsFavourited(favs.includes(id));
    }
  }, [user, id, notifications?.preferences?.favouriteBusinessIds]);

  // Iter 178 — Deep-link handler for offer/event share URLs.
  // When the URL hash is `#offer-{id}` or `#event-{id}` and the matching
  // record has finished loading, scroll the card into view smoothly and
  // flash a highlight ring. Runs whenever offers/events change so it
  // also catches the case where the page mounts before the data lands.
  // Iter 179 — also flips `deepLinkMissing` when the hash points at a
  // missing offer/event after data has loaded, so the page can render
  // a friendly fallback banner.
  useEffect(() => {
    const hash = (typeof window !== 'undefined' && window.location.hash) || '';
    if (!hash) { setDeepLinkMissing(false); return; }
    let targetId = null;
    let kind = null;
    let rawId = null;
    if (hash.startsWith('#offer-')) {
      rawId = decodeURIComponent(hash.slice('#offer-'.length));
      kind = 'offer';
      if (businessOffers.some((o) => o.id === rawId)) targetId = `offer-${rawId}`;
    } else if (hash.startsWith('#event-')) {
      rawId = decodeURIComponent(hash.slice('#event-'.length));
      kind = 'event';
      if (businessEvents.some((e) => e.id === rawId)) targetId = `event-${rawId}`;
    }
    if (!kind) { setDeepLinkMissing(false); return; }
    if (!targetId) {
      // Hash matched the pattern but no card found. Only declare missing
      // after the data layer has settled to avoid a flicker during the
      // initial render before fetches resolve.
      if (offersEventsLoaded) setDeepLinkMissing(true);
      return;
    }
    // Match found → clear any prior missing flag and trigger scroll+highlight.
    setDeepLinkMissing(false);
    const t1 = setTimeout(() => {
      const el = document.getElementById(targetId);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedItemId(targetId);
    }, 120);
    const t2 = setTimeout(() => setHighlightedItemId(null), 3200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [businessOffers, businessEvents, offersEventsLoaded]);


  const handleToggleFavourite = async () => {
    // Logged-in users keep the account-bound endpoint.
    if (user) {
      const token = getToken();
      try {
        if (isFavourited) {
          await axios.delete(`${API}/user/favorites/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          setIsFavourited(false);
        } else {
          await axios.post(`${API}/user/favorites/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
          setIsFavourited(true);
          try {
            if (!sessionStorage.getItem('f2d:fav-toast-shown')) {
              toast(t('favouriteToast', "🔔 You'll be notified about new events and offers from this business."), { duration: 4000, id: 'favourite-toast' });
              sessionStorage.setItem('f2d:fav-toast-shown', '1');
            }
            window.dispatchEvent(new Event('inbox:refresh'));
          } catch (_) { /* noop */ }
        }
      } catch (err) {
        console.error('Favourite toggle error:', err);
      }
      return;
    }
    // Anonymous flow — Phase 1 (no email needed). Ensure a device
    // subscription, then toggle via /api/notifications/favourite.
    try {
      const subId = await notifications.ensureSubscription();
      if (!subId) {
        toast(t('followNeedsNotifications', 'Allow notifications to follow businesses.'), { duration: 3000 });
        return;
      }
      const wasFav = isFavourited;
      setIsFavourited(!wasFav);  // optimistic
      const data = await notifications.toggleFavourite(id);
      if (data === null) {
        setIsFavourited(wasFav);  // roll back
        toast(t('couldNotSave', 'Could not save — please try again.'), { duration: 3000 });
      } else if (data.favourited) {
        try {
          if (!sessionStorage.getItem('f2d:fav-toast-shown')) {
            toast(t('favouriteToast', "🔔 You'll be notified about new events and offers from this business."), { duration: 4000, id: 'favourite-toast' });
            sessionStorage.setItem('f2d:fav-toast-shown', '1');
          }
          window.dispatchEvent(new Event('inbox:refresh'));
        } catch (_) { /* noop */ }
      }
    } catch (err) {
      console.error('Anonymous favourite toggle error:', err);
    }
  };

  const fetchBusiness = async () => {
    try {
      setLoading(true);
      // Iter 159 — fail fast on malformed URLs so we surface the friendly
      // broken-link page instead of waiting on a guaranteed 404.
      if (isInvalidId) {
        setBusiness(null);
        setError(t('invalidBusinessLink', 'Sorry, this business link appears to be invalid or expired.'));
        setLoading(false);
        return;
      }
      // Draft preview: render the unsaved snapshot from the dashboard without an API call.
      if (isDraft) {
        const draft = readDraftPreview(id);
        if (draft) {
          setBusiness(draft);
          setError(null);
          setLoading(false);
          return;
        }
      }
      const response = await axios.get(`${API}/business/${id}`);
      setBusiness(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching business:', error);
      if (error?.response?.status === 410) {
        setError(t('listingUnavailable', 'This business listing is no longer available.'));
      } else {
        setError(t('businessNotFound'));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/business/${id}/reviews`);
      setReviewData(res.data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  };

  const fetchBusinessOffers = async () => {
    try {
      const res = await axios.get(`${API}/offers`);
      setBusinessOffers((res.data || []).filter(o => o.businessId === id));
    } catch (err) {
      console.error('Error fetching offers:', err);
    } finally {
      // Iter 179 — both fetches set this flag in their finally; either one
      // landing flips it on (idempotent). Used by the deep-link "missing"
      // detector to wait until the data layer has settled.
      setOffersEventsLoaded(true);
    }
  };

  const fetchBusinessEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setBusinessEvents((res.data || []).filter(e => e.businessId === id));
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setOffersEventsLoaded(true);
    }
  };

  const handleReviewSubmit = async () => {
    setReviewError('');
    if (!reviewForm.rating) { setReviewError(t('ratingRequired')); return; }
    if (reviewForm.comment.length < 10) { setReviewError(t('reviewTextMin')); return; }
    if (reviewForm.comment.length > 500) { setReviewError(t('reviewTextMax')); return; }
    setReviewSubmitting(true);
    try {
      await axiosInstance.post('/reviews', {
        businessId: id,
        rating: reviewForm.rating,
        comment: reviewForm.comment
      });
      setReviewSuccess(true);
      setShowReviewForm(false);
      setReviewForm({ rating: 0, comment: '' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('already reviewed')) {
        setReviewError(t('alreadyReviewed'));
      } else {
        setReviewError(typeof detail === 'string' ? detail : t('error'));
      }
    } finally {
      setReviewSubmitting(false);
    }
  };

  const trackAction = (actionType) => {
    axios.post(`${API}/listings/${id}/action`, { actionType, sessionId }).catch(() => {});
  };

  const handleClaimSubmit = async () => {
    setClaimSubmitting(true);
    setClaimError('');
    try {
      await axiosInstance.post('/claims', { businessId: id, message: claimMessage });
      setClaimSuccess(true);
      setClaimStatus('pending');
      setShowClaimForm(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('pending')) {
        setClaimError(t('claimAlreadyPending'));
      } else if (typeof detail === 'string' && detail.includes('already claimed')) {
        setClaimError(t('claimAlreadyClaimed'));
      } else {
        setClaimError(t('claimError'));
      }
    } finally {
      setClaimSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error || !business) {
    const friendlyMessage = isInvalidId
      ? t('invalidBusinessLink', 'Sorry, this business link appears to be invalid or expired.')
      : (error || t('businessNotFound', 'Business profile not found'));
    return (
      <div className="min-h-screen bg-gray-50" data-testid="business-broken-link-page">
        <div className="max-w-2xl mx-auto px-4 py-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
            data-testid="broken-link-back"
          >
            <ArrowLeft className="h-5 w-5 mr-2 rtl-flip" />
            {t('back', 'Back')}
          </button>
          <div className="bg-white rounded-2xl shadow-md p-8 text-center">
            <div className="mx-auto mb-5 h-16 w-16 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
              <Search className="h-8 w-8 text-amber-500" aria-hidden="true" />
            </div>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 mb-2" data-testid="broken-link-title">
              {t('brokenLinkTitle', 'This business link doesn\'t work')}
            </h1>
            <p className="text-sm text-gray-600 leading-relaxed mb-6 max-w-md mx-auto" data-testid="broken-link-message">
              {friendlyMessage}
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <button
                onClick={() => navigate('/discover')}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                data-testid="broken-link-browse"
              >
                {t('browseBusinesses', 'Browse Businesses')}
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                data-testid="broken-link-home"
              >
                {t('returnHome', 'Return Home')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Preview Mode Banner */}
      {isPreview && (
        <div
          className={`sticky top-0 z-20 text-white px-4 py-2.5 flex items-center justify-between ${isDraft ? 'bg-amber-600' : 'bg-purple-700'}`}
          data-testid={isDraft ? 'draft-preview-banner' : 'preview-mode-banner'}
        >
          <p className="text-sm font-medium">
            {isDraft
              ? t('draftPreviewBanner', 'Preview — not saved. Go back and click Save to apply these changes.')
              : t('previewBanner', 'This is how your listing appears to the public')}
          </p>
          <button
            onClick={() => {
              if (isDraft) clearDraftPreview(id);
              if (window.opener) { window.close(); } else { navigate('/business/dashboard'); }
            }}
            className={`px-3 py-1 text-xs font-bold rounded-lg transition-colors ${isDraft ? 'bg-white text-amber-700 hover:bg-gray-100' : 'bg-white text-purple-700 hover:bg-gray-100'}`}
            data-testid="back-to-dashboard-btn"
          >
            {isDraft ? t('closePreview', 'Close Preview') : t('backToDashboard', 'Back to Dashboard')}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5 mr-2 rtl-flip" />
            {t('goBack')}
          </button>
          {notifications?.isSubscribed && (
            <button
              onClick={handleToggleFavourite}
              className={`p-2 rounded-full transition-colors ${isFavourited ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="favorite-btn"
            >
              <Heart className={`h-5 w-5 ${isFavourited ? 'fill-current' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* TOP CLAIM BANNER — prominent, conversion-focused */}
      {business.source === 'seed' && !business.isClaimed && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="claim-top-banner">
          <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 flex-shrink-0" />
              <p className="font-semibold text-sm sm:text-base">{t('claimTopBannerText')}</p>
            </div>
            {user ? (
              <button
                onClick={() => document.getElementById('claim-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="whitespace-nowrap px-5 py-2 bg-white text-amber-600 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                data-testid="claim-top-banner-btn"
              >
                {t('claimNowBtn')}
              </button>
            ) : (
              <button
                onClick={() => navigate('/signin')}
                className="whitespace-nowrap px-5 py-2 bg-white text-amber-600 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                data-testid="claim-top-banner-signin-btn"
              >
                {t('signInToClaim')}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Iter 179 — Friendly fallback banner when a shared `#offer-...`
            or `#event-...` deep link points at an item that no longer
            exists on this business (deleted / expired / hidden). */}
        {deepLinkMissing && (
          <div
            className="mb-4 flex items-start gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 shadow-sm"
            role="status"
            data-testid="deeplink-missing-banner"
          >
            <span className="text-xl leading-none">ℹ️</span>
            <div className="flex-1">
              <p className="text-sm font-medium">
                {t(
                  'deeplinkMissing',
                  'That offer or event is no longer available — here\u2019s the business instead.'
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDeepLinkMissing(false)}
              className="text-amber-700 hover:text-amber-900 text-sm font-semibold"
              aria-label={t('dismiss', 'Dismiss')}
              data-testid="deeplink-missing-dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Temporary closure / business status banner (overrides opening hours
            for the affected time only — computed server-side, Europe/Madrid). */}
        {business.closureStatus && (
          <div
            className={`mb-4 flex items-start gap-3 p-3 rounded-lg border shadow-sm ${
              business.closureStatus.state === 'closed'
                ? 'border-red-300 bg-red-50 text-red-800'
                : 'border-amber-300 bg-amber-50 text-amber-900'
            }`}
            role="status"
            data-testid="closure-status-banner"
          >
            <CalendarOff className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold" data-testid="closure-status-message">{business.closureStatus.message}</p>
              {business.closureStatus.reason && (
                <p className="text-sm opacity-90">{business.closureStatus.reason}</p>
              )}
            </div>
          </div>
        )}

        {/* Upcoming (future-dated) closure notice — informational only; does not
            affect open/closed status, ranking or the Open Now filter. */}
        {business.upcomingClosure && (
          <div
            className="mb-4 flex items-start gap-3 p-3 rounded-lg border border-blue-300 bg-blue-50 text-blue-900 shadow-sm"
            role="status"
            data-testid="upcoming-closure-banner"
          >
            <CalendarClock className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide opacity-70">Upcoming closure</p>
              <p className="text-sm font-semibold" data-testid="upcoming-closure-message">{business.upcomingClosure.message}</p>
              {business.upcomingClosure.reason && (
                <p className="text-sm opacity-90">{business.upcomingClosure.reason}</p>
              )}
            </div>
          </div>
        )}
        {(() => {
          const isClaimedOrOwned =
            business.ownershipStatus === 'claimed' ||
            business.ownershipStatus === 'owner_verified' ||
            business.isClaimed === true ||
            business.source === 'owner' ||
            business.source === 'founders' ||
            !!business.userId;
          const isSeededUnclaimed =
            !isClaimedOrOwned && (
              business.source === 'seed' ||
              business.source === 'seeded' ||
              business.ownershipStatus === 'seeded' ||
              business.ownershipStatus === 'unclaimed'
            );
          if (!isSeededUnclaimed) return null;
          return (
            <div className="mb-4">
              <SeededListingNotice
                ownershipStatus={business.ownershipStatus}
                source={business.source}
                isClaimed={business.isClaimed}
                userId={business.userId}
                businessName={business.businessName}
                onClaim={() => {
                  if (user) document.getElementById('claim-section')?.scrollIntoView({ behavior: 'smooth' });
                  else navigate('/signin');
                }}
                onRequestRemoval={() => { setRemovalDefaultType('removal'); setRemovalModalOpen(true); }}
              />
            </div>
          );
        })()}

        {/* Photos */}
        {business.photos && business.photos.length > 0 && (
          <div className="mb-6">
            <div className={`grid gap-3 ${business.photos.length === 1 ? 'grid-cols-1' : business.photos.length === 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`}>
              {business.photos.map((photo, index) => (
                <div
                  key={index}
                  className={`relative rounded-xl overflow-hidden shadow-md cursor-pointer bg-gray-50 ${
                    business.photos.length > 2 && index === 0 ? 'col-span-2 row-span-2' : ''
                  }`}
                  onClick={() => setFullscreenPhoto(photo)}
                >
                  <img
                    src={photo}
                    alt={`${business.businessName} photo ${index + 1}`}
                    className={`w-full rounded-xl object-contain bg-gray-50 ${
                      business.photos.length === 1 ? 'max-h-[40rem] aspect-[3/4]' :
                      business.photos.length > 2 && index === 0 ? 'aspect-[3/4]' : 'aspect-[3/4]'
                    }`}
                    data-testid={`business-photo-${index}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header section */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 text-white">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                {business.logoUrl && (
                  <img
                    src={business.logoUrl}
                    alt={`${business.businessName} logo`}
                    className="h-12 w-12 rounded-lg object-cover bg-white/20 border border-white/30 shrink-0"
                    data-testid="business-logo"
                  />
                )}
                <h1 className="text-2xl md:text-3xl font-bold" data-testid="business-name">
                  {business.businessName}
                </h1>
                <FoundingBusinessBadge
                  plan={business.ownershipStatus === 'owner_verified' ? business.subscriptionPlan : null}
                  variant="profile"
                />
              </div>
              {/* Badge: Charity, Unclaimed or Verified */}
              {business.listingType === 'charity' ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-500/90 text-white text-xs font-semibold rounded-full whitespace-nowrap" data-testid="charity-detail-badge">
                  <Heart className="h-3.5 w-3.5" />
                  {t('charity.charityBadge')}
                </span>
              ) : business.isClaimed || business.ownershipStatus === 'owner_verified' || business.ownershipStatus === 'claimed' ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-500/90 text-white text-xs font-semibold rounded-full whitespace-nowrap" data-testid="verified-badge">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {t('verifiedOwner')}
                </span>
              ) : business.source === 'seed' || business.ownershipStatus === 'seeded' || business.ownershipStatus === 'unclaimed' ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/90 text-white text-xs font-semibold rounded-full whitespace-nowrap" data-testid="unclaimed-badge">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {t('unclaimedListing')}
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm" data-testid="business-category">
                {CATEGORY_I18N_MAP[business.category] ? t(CATEGORY_I18N_MAP[business.category], business.category) : business.category}
              </span>
              {business.venueType?.map(v => (
                <span key={v} className="px-3 py-1 bg-white/10 rounded-full text-sm">{tf(t, v)}</span>
              ))}
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="p-4 border-b bg-gray-50">
            {business.source === 'seed' && !business.isClaimed ? (
              <div data-testid="unclaimed-contact-notice">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <ShieldCheck className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  <p className="text-sm text-gray-500">{t('unclaimedContactNotice')}</p>
                </div>
                <button
                  onClick={() => document.getElementById('claim-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors font-semibold"
                  data-testid="claim-cta-button"
                >
                  <ShieldCheck className="h-5 w-5" />
                  {t('claimToUnlock')}
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                <a
                  href={`tel:${business.phone}`}
                  onClick={() => trackAction('call')}
                  className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors font-medium"
                  data-testid="call-button"
                >
                  <Phone className="h-5 w-5" />
                  {t('callNow')}
                </a>
                <a
                  href={`mailto:${business.email}`}
                  onClick={() => trackAction('email')}
                  className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-3 border-2 border-purple-600 text-purple-600 rounded-xl hover:bg-purple-50 transition-colors font-medium"
                  data-testid="email-button"
                >
                  <Mail className="h-5 w-5" />
                  {t('emailButton')}
                </a>
                {business.whatsapp && (
                  <a
                    href={`https://wa.me/${business.whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackAction('whatsapp')}
                    className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium"
                    data-testid="whatsapp-button"
                  >
                    <MessageCircle className="h-5 w-5" />
                    WhatsApp
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Favourite & Follow Buttons — anonymous-friendly since iter 137/142.
              Anonymous users still see and can interact with these — the
              underlying handlers create / read a device-bound favourite,
              and the FE asks for push permission only when needed. */}
          <div className="px-4 py-3 border-b flex gap-2" data-testid="fav-follow-bar">
            <button
              onClick={handleToggleFavourite}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isFavourited
                  ? 'bg-red-50 text-red-600 border border-red-200'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
              data-testid="favourite-btn"
            >
              <Heart className={`h-4 w-4 ${isFavourited ? 'fill-current' : ''}`} />
              {isFavourited ? t('favourited') : t('favourite')}
            </button>
            {notifications?.isSubscribed ? (
              <button
                onClick={async () => {
                  // Ensure the device is registered first so the follow is never
                  // dropped on a first-tap race (backend also upserts now).
                  await notifications.ensureSubscription();
                  const wasFollowing = notifications.preferences.followedBusinessIds.includes(id);
                  const result = await notifications.toggleFollow(id);
                  if (result?.following && !wasFollowing) {
                    setFollowToast(true);
                    setTimeout(() => setFollowToast(false), 3500);
                  }
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  notifications.preferences.followedBusinessIds.includes(id)
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'bg-purple-600 text-white shadow-sm hover:bg-purple-700'
                }`}
                data-testid="follow-btn"
              >
                {notifications.preferences.followedBusinessIds.includes(id)
                  ? <BellRing className="h-4 w-4" />
                  : <Bell className="h-4 w-4" />
                }
                {notifications.preferences.followedBusinessIds.includes(id) ? t('following') : t('followAlerts')}
              </button>
            ) : notifications?.sdkAvailable ? (
              <button
                onClick={async () => { await notifications?.enableNotifications(); }}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg text-sm hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200 transition-colors"
                data-testid="enable-notifications-btn"
              >
                <Bell className="h-4 w-4" />
                {t('enableNotifications')}
              </button>
            ) : null}
          </div>

          {/* First Follow Toast */}
          {followToast && (
            <div className="mx-4 mb-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 animate-slide-up" data-testid="first-follow-toast">
              <BellRing className="h-4 w-4 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700 font-medium">{t('firstFollowBoost')}</p>
            </div>
          )}

          {/* Share Buttons */}
          <ShareButtons
            businessId={id}
            businessName={business.businessName}
            description={i18n.language.startsWith('es') ? business.description_es : business.description_en}
          />

          {/* Details section */}
          <div className="p-6 space-y-6">
            {/* Category + Summary */}
            {business.category && (
              <div>
                <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full mb-2" data-testid="business-category-badge">
                  {business.category}
                </span>
              </div>
            )}

            {/* Description */}
            {(business.description_en || business.description_es || business.description) && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('about')}</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap" data-testid="business-description">
                  {(i18n.language.startsWith('es') ? (business.description_es || business.description || business.description_en) : (business.description_en || business.description || business.description_es))}
                </p>
              </div>
            )}

            {/* Features Tags */}
            {(business.additionalFeatures?.length > 0 || business.accessibilityFeatures?.length > 0 || business.dietaryOptions?.length > 0) && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('featuresAmenities')}</h2>
                <div className="flex flex-wrap gap-2">
                  {business.additionalFeatures?.map(f => (
                    <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm">
                      {f.includes('Kid') && <Baby className="h-4 w-4" />}
                      {f.includes('Pet') && '🐾'}
                      {tf(t, f)}
                    </span>
                  ))}
                  {business.accessibilityFeatures?.map(f => (
                    <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm">
                      {f.includes('Wheelchair') && <Accessibility className="h-4 w-4" />}
                      {f.includes('Parking') && <ParkingCircle className="h-4 w-4" />}
                      {tf(t, f)}
                    </span>
                  ))}
                  {business.dietaryOptions?.map(d => (
                    <span key={d} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm">
                      <Utensils className="h-4 w-4" />
                      {tf(t, d)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Group Sizes */}
            {business.groupSizes?.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('groupSizesWelcome')}</h2>
                <div className="flex flex-wrap gap-2">
                  {business.groupSizes.map(size => (
                    <span key={size} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm">
                      <Users className="h-4 w-4" />
                      {tf(t, size)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <hr className="border-gray-200" />

            {/* Active Offers */}
            {businessOffers.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Percent className="h-5 w-5 text-orange-500" />
                  {t('offers', 'Offers')}
                </h2>
                <div className="space-y-2">
                  {businessOffers.map(offer => (
                    <div
                      key={offer.id}
                      id={`offer-${offer.id}`}
                      className={`p-3 bg-orange-50 border rounded-lg transition-shadow ${
                        highlightedItemId === `offer-${offer.id}`
                          ? 'border-orange-500 ring-2 ring-orange-400 ring-offset-2 shadow-lg animate-pulse'
                          : 'border-orange-200'
                      }`}
                      data-testid={`listing-offer-${offer.id}`}
                    >
                      <div className="flex gap-3">
                        {offer.image && (
                          <img
                            src={offer.image}
                            alt={offer.title}
                            loading="lazy"
                            className="h-20 w-20 object-cover rounded-md flex-shrink-0 border border-orange-200"
                            data-testid={`offer-image-${offer.id}`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">{offer.title}</h4>
                              <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-wrap" data-testid={`offer-description-${offer.id}`}>{offer.description}</p>
                            </div>
                            {offer.discountPercentage && (
                              <span className="flex-shrink-0 ml-2 px-2.5 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">
                                {offer.discountPercentage}% off
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-1.5 gap-2 flex-wrap">
                        <p className="text-xs text-gray-400">
                          {t('validUntil', 'Valid until')}: {new Date(offer.validUntil).toLocaleDateString()}
                          {(() => {
                            const stamp = formatTimeAgo(offer.updatedAt || offer.createdAt);
                            return stamp ? (
                              <span className="ml-2 text-gray-400" data-testid={`offer-updated-${offer.id}`}>
                                · Last updated {stamp}
                              </span>
                            ) : null;
                          })()}
                        </p>
                        {/* Iter 172 — inline share button for offers */}
                        <ShareInlineButton
                          url={`${typeof window !== 'undefined' ? window.location.origin : 'https://www.find-2-day.com'}/api/share/offers/${encodeURIComponent(offer.id)}`}
                          title={`${offer.title} – ${business.businessName}`}
                          text={offer.description || t('shareDefaultText', 'Check out this on Find-2-Day')}
                          testId={`offer-${offer.id}`}
                        />
                      </div>
                      {/* Iter 182 — owner/admin inline controls (Edit / Duplicate / Delete) */}
                      <OwnerInlineControls
                        kind="offer"
                        itemId={offer.id}
                        itemTitle={offer.title}
                        user={user}
                        business={business}
                        businessId={business.id}
                        isPreview={isPreview}
                        onDeleted={(deletedId) =>
                          setBusinessOffers((prev) => prev.filter((o) => o.id !== deletedId))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Events */}
            {businessEvents.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-purple-500" />
                  {t('events', 'Events')}
                </h2>
                <div className="space-y-2">
                  {businessEvents.map(event => (
                    <div
                      key={event.id}
                      id={`event-${event.id}`}
                      className={`p-3 bg-purple-50 border rounded-lg transition-shadow ${
                        highlightedItemId === `event-${event.id}`
                          ? 'border-purple-500 ring-2 ring-purple-400 ring-offset-2 shadow-lg animate-pulse'
                          : 'border-purple-200'
                      }`}
                      data-testid={`listing-event-${event.id}`}
                    >
                      <div className="flex gap-3">
                        {event.image && (
                          <img
                            src={event.image}
                            alt={event.title}
                            loading="lazy"
                            className="h-20 w-20 object-cover rounded-md flex-shrink-0 border border-purple-200"
                            data-testid={`event-image-${event.id}`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-medium text-gray-900 flex-1">{event.title}</h4>
                            <AddToCalendarButton event={event} business={business} variant="small" />
                          </div>
                          {event.description && <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-wrap" data-testid={`event-description-${event.id}`}>{event.description}</p>}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-500">
                        <span>{new Date(event.startDate).toLocaleDateString()} {new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {event.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{formatPlaceName(event.location)}</span>}
                        {event.price != null && event.price > 0 && <span className="font-medium text-purple-600">{'\u20AC'}{event.price.toFixed(2)}</span>}
                        {(() => {
                          const stamp = formatTimeAgo(event.updatedAt || event.createdAt);
                          return stamp ? (
                            <span className="text-gray-400" data-testid={`event-updated-${event.id}`}>
                              · Last updated {stamp}
                            </span>
                          ) : null;
                        })()}
                        {/* Iter 172 — inline share button for events */}
                        <EventShareControl
                          url={`${typeof window !== 'undefined' ? window.location.origin : 'https://www.find-2-day.com'}/api/share/events/${encodeURIComponent(event.id)}`}
                          title={`${event.title} – ${business.businessName}`}
                          text={event.description || t('shareDefaultText', 'Check out this on Find-2-Day')}
                          testId={`event-${event.id}`}
                          className="ml-auto"
                        />
                      </div>
                      {/* Iter 182 — owner/admin inline controls (Edit / Duplicate / Delete) */}
                      <OwnerInlineControls
                        kind="event"
                        itemId={event.id}
                        itemTitle={event.title}
                        user={user}
                        business={business}
                        businessId={business.id}
                        isPreview={isPreview}
                        onDeleted={(deletedId) =>
                          setBusinessEvents((prev) => prev.filter((e) => e.id !== deletedId))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <hr className="border-gray-200" />

            {/* Contact Information */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('contactInfo')}</h2>
              <div className="space-y-4">
                {/* Address — unclaimed: city only */}
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MapPin className="h-5 w-5 text-purple-600" />
                  </div>
                  {business.source === 'seed' && !business.isClaimed ? (
                    <div>
                      <p className="text-gray-700 font-medium" data-testid="business-city">{formatPlaceName(business.city)}</p>
                      {[business.province, business.region, business.country].filter(Boolean).length > 0 && (
                        <p className="text-gray-500 text-sm" data-testid="business-location-detail">
                          {[business.province, business.region, business.country].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((business.address || '') + ', ' + (business.city || ''))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackAction('map')}
                        className="text-purple-600 hover:underline font-medium"
                        data-testid="business-address"
                      >
                        {business.address}
                      </a>
                      <p className="text-gray-600" data-testid="business-city">{formatPlaceName(business.city)}</p>
                      {[business.province, business.region, business.postcode, business.country].filter(Boolean).length > 0 && (
                        <p className="text-gray-500 text-sm" data-testid="business-location-detail">
                          {[business.province, business.region, business.postcode, business.country].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Stage 2B — Leaflet/OSM map (only when the listing has coordinates) */}
                {typeof business.latitude === 'number' && typeof business.longitude === 'number' && (
                  <div className="pl-0 sm:pl-11 space-y-2" data-testid="business-detail-map-wrap">
                    <BusinessMap
                      markers={[{ id: business.id, lat: business.latitude, lng: business.longitude, title: business.businessName }]}
                      height={260}
                    />
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${business.latitude},${business.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackAction('map')}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                      data-testid="business-directions-btn"
                    >
                      <Navigation className="h-4 w-4" />
                      {t('directionsBtn', 'Directions')}
                    </a>
                  </div>
                )}

                {/* Phone — unclaimed: masked */}
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Phone className="h-5 w-5 text-purple-600" />
                  </div>
                  {business.source === 'seed' && !business.isClaimed ? (
                    <span className="text-gray-400 font-medium" data-testid="business-phone-masked">
                      {maskPhone(business.phone)}
                    </span>
                  ) : (
                    <a
                      href={`tel:${business.phone}`}
                      onClick={() => trackAction('call')}
                      className="text-purple-600 hover:underline font-medium"
                      data-testid="business-phone"
                    >
                      {formatPhone(business.phone, business.countryCode).formatted || business.phone}
                    </a>
                  )}
                </div>

                {/* Email — unclaimed: hidden */}
                {(business.isClaimed || !(business.source === 'seed' && !business.isClaimed)) && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Mail className="h-5 w-5 text-purple-600" />
                    </div>
                    <a
                      href={`mailto:${business.email}`}
                      onClick={() => trackAction('email')}
                      className="text-purple-600 hover:underline font-medium"
                      data-testid="business-email"
                    >
                      {business.email}
                    </a>
                  </div>
                )}

                {/* Website — unclaimed: hidden */}
                {business.website && !(business.source === 'seed' && !business.isClaimed) && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Globe className="h-5 w-5 text-purple-600" />
                    </div>
                    <a
                      href={business.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackAction('website')}
                      className="text-purple-600 hover:underline font-medium"
                      data-testid="business-website"
                    >
                      {t('visitWebsite')}
                    </a>
                  </div>
                )}

                {business.openingHours && business.openingHours !== 'Closed' && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Clock className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 mb-1">{t('openingHours')}</p>
                      <p className="text-gray-600 whitespace-pre-line text-sm" data-testid="business-hours">
                        {business.openingHours.replace(/; /g, '\n')}
                      </p>
                      {business.specialHours && (
                        <p className="text-gray-500 text-sm mt-1.5" data-testid="business-special-hours">
                          <span className="font-medium text-gray-700">{t('specialHours', 'Special / holiday hours')}: </span>
                          {business.specialHours}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {!business.openingHours && business.specialHours && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Clock className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 mb-1">{t('specialHours', 'Special / holiday hours')}</p>
                      <p className="text-gray-600 text-sm" data-testid="business-special-hours">{business.specialHours}</p>
                    </div>
                  </div>
                )}

                {business.socialLinks && Object.values(business.socialLinks).some(Boolean)
                  && !(business.source === 'seed' && !business.isClaimed) && (
                  <div className="flex items-center gap-3" data-testid="business-social-links">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Share2 className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {[
                        ['instagram', Instagram], ['facebook', Facebook],
                        ['tiktok', Globe], ['twitter', Globe], ['youtube', Youtube],
                      ].map(([key, Icon]) => {
                        const url = business.socialLinks[key];
                        if (!url) return null;
                        return (
                          <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                            onClick={() => trackAction('social')}
                            className="p-2 rounded-lg bg-gray-100 hover:bg-purple-100 text-gray-600 hover:text-purple-700 transition-colors"
                            title={key} data-testid={`business-social-${key}`}>
                            <Icon className="h-4 w-4" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Reviews Section */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6" data-testid="reviews-section">
            <div className="p-5 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-gray-900">{t('reviews')}</h2>
                  {reviewData.totalCount > 0 && (
                    <div className="flex items-center gap-1.5 text-sm">
                      <div className="flex items-center gap-0.5 bg-yellow-50 px-2 py-0.5 rounded-full">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-semibold text-yellow-700">{reviewData.averageRating}</span>
                      </div>
                      <span className="text-gray-500">({t('reviewCount', { count: reviewData.totalCount })})</span>
                    </div>
                  )}
                </div>
                {user && user.role === 'user' && !reviewSuccess && !showReviewForm && (
                  <button
                    onClick={() => setShowReviewForm(true)}
                    className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                    data-testid="write-review-btn"
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    {t('writeReview')}
                  </button>
                )}
              </div>
            </div>

            {/* Review Form */}
            {showReviewForm && (
              <div className="p-5 border-b bg-purple-50/50" data-testid="review-form">
                <h3 className="font-medium text-gray-900 mb-3">{t('writeReview')}</h3>
                {/* Star Rating */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('yourRating')}</label>
                  <div className="flex gap-1" data-testid="star-rating-input">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm(f => ({ ...f, rating: star }))}
                        className="p-0.5 transition-transform hover:scale-110"
                        data-testid={`star-${star}`}
                      >
                        <Star className={`h-7 w-7 ${reviewForm.rating >= star ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                {/* Review Text */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('yourReview')}</label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                    placeholder={t('reviewPlaceholder')}
                    maxLength={500}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                    data-testid="review-text-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">{t('charsRemaining', { count: 500 - reviewForm.comment.length })}</p>
                </div>
                {reviewError && <p className="text-sm text-red-600 mb-3" data-testid="review-error">{reviewError}</p>}
                <div className="flex gap-2">
                  <button onClick={() => { setShowReviewForm(false); setReviewError(''); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleReviewSubmit}
                    disabled={reviewSubmitting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                    data-testid="submit-review-btn"
                  >
                    {reviewSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {reviewSubmitting ? t('submittingReview') : t('submitReview')}
                  </button>
                </div>
              </div>
            )}

            {/* Success message */}
            {reviewSuccess && (
              <div className="p-4 bg-green-50 border-b" data-testid="review-success">
                <p className="text-sm text-green-700 font-medium">{t('reviewSubmitted')}</p>
              </div>
            )}

            {/* Login prompt */}
            {!user && (
              <div className="p-4 border-b text-center">
                <p className="text-sm text-gray-500">{t('loginToReview')}</p>
              </div>
            )}

            {/* Reviews List */}
            <div className="p-5">
              {reviewData.reviews.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4" data-testid="no-reviews">{t('noReviewsYet')}</p>
              ) : (
                <div className="space-y-4">
                  {reviewData.reviews.map(review => (
                    <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0" data-testid={`review-${review.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map(s => (
                              <Star key={s} className={`h-4 w-4 ${review.rating >= s ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-900">{review.userName}</span>
                        </div>
                        <span className="text-xs text-gray-400">{new Date(review.createdAt).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-gray-600">{review.comment}</p>
                      {user && (
                        <button
                          onClick={() => setReportModal({ open: true, targetType: 'review', targetId: review.id, targetName: `${t('reviewBy')} ${review.userName}` })}
                          className="mt-2 text-xs text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1"
                          data-testid={`report-review-btn-${review.id}`}
                        >
                          <Flag className="h-3 w-3" />
                          {t('reportReview')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Report Listing */}
          <div className="p-4 border-t text-center">
            {user ? (
              <button
                onClick={() => setReportModal({ open: true, targetType: 'business', targetId: business.id, targetName: business.businessName })}
                className="text-sm text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1 mx-auto"
                data-testid="report-listing-btn"
              >
                <Flag className="h-3.5 w-3.5" />
                {t('reportListing')}
              </button>
            ) : (
              <p className="text-sm text-gray-400">{t('loginToReport')}</p>
            )}
          </div>
        </div>

        {/* Business Claim Section */}
        {business.source === 'seed' && !business.isClaimed && (
          <div id="claim-section" className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 shadow-sm" data-testid="claim-business-section">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-3 bg-amber-100 rounded-full">
                <ShieldCheck className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1">
                {/* Data source transparency */}
                <p className="text-sm text-amber-700 mb-2" data-testid="seeded-data-notice">
                  {t('seededDataNotice', 'Information may be sourced from public data.')}
                </p>
                <p className="text-amber-800 font-semibold text-base mb-1">{t('claimBanner')}</p>

                {/* Claim status messages */}
                {claimSuccess && (
                  <div className={`mt-3 p-3 rounded-lg ${claimStatus === 'approved' ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="claim-success-msg">
                    {claimStatus === 'approved' ? (
                      <>
                        <p className="text-green-700 font-medium">{t('claimAutoApproved')}</p>
                        <p className="text-green-600 text-sm">{t('claimAutoApprovedDesc')}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-amber-700 font-medium">{t('claimSubmitted')}</p>
                        <p className="text-amber-600 text-sm">{t('claimSubmittedDesc')}</p>
                      </>
                    )}
                  </div>
                )}

                {claimStatus === 'pending' && !claimSuccess && (
                  <p className="mt-2 text-amber-600 text-sm italic" data-testid="claim-pending-msg">{t('claimStatusPending')}</p>
                )}
                {claimStatus === 'approved' && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="claim-approved-msg">
                    <p className="text-green-700 font-medium">{t('claimStatusApproved', 'Your claim has been approved!')}</p>
                    <p className="text-green-600 text-sm mt-1">{t('claimApprovedDesc', 'You now have business owner access. Manage your listing from the dashboard.')}</p>
                    <button
                      onClick={() => navigate('/business/dashboard')}
                      className="mt-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                      data-testid="go-to-dashboard-btn"
                    >
                      {t('goToDashboard', 'Go to Business Dashboard')}
                    </button>
                  </div>
                )}
                {claimStatus === 'rejected' && (
                  <p className="mt-2 text-red-600 text-sm" data-testid="claim-rejected-msg">{t('claimStatusRejected')}</p>
                )}

                {/* Enhanced Claim Form */}
                {showClaimForm && (
                  <ClaimForm
                    businessId={id}
                    businessName={business.businessName}
                    onSuccess={(claimData) => {
                      setClaimSuccess(true);
                      setShowClaimForm(false);
                      if (claimData?.status === 'approved') {
                        setClaimStatus('approved');
                      } else {
                        setClaimStatus('pending');
                      }
                    }}
                    onCancel={() => setShowClaimForm(false)}
                  />
                )}

                {/* CTA button — Claim this Business is retired (Stage 1). */}
                {false && !showClaimForm && !claimSuccess && claimStatus !== 'pending' && claimStatus !== 'approved' && (
                  user ? (
                    <button
                      onClick={() => setShowClaimForm(true)}
                      className="mt-3 px-5 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
                      data-testid="claim-business-btn"
                    >
                      {t('claimThisBusiness')}
                    </button>
                  ) : (
                    <p className="mt-2 text-amber-600 text-sm italic">{t('loginToClaim')}</p>
                  )
                )}

                {/* Request correction/removal link */}
                <button
                  onClick={() => navigate('/data-deletion')}
                  className="mt-3 text-xs text-amber-600 hover:text-amber-800 underline"
                  data-testid="request-removal-btn"
                >
                  {t('requestCorrectionRemoval', 'Request correction or removal')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ReportModal
        isOpen={reportModal.open}
        onClose={() => setReportModal({ open: false, targetType: '', targetId: '', targetName: '' })}
        targetType={reportModal.targetType}
        targetId={reportModal.targetId}
        targetName={reportModal.targetName}
      />

      {/* Fullscreen photo lightbox */}
      {fullscreenPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setFullscreenPhoto(null)}
          data-testid="photo-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2"
            onClick={() => setFullscreenPhoto(null)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <img
            src={fullscreenPhoto}
            alt="Full size"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Iter 128 — Removal / edit request modal */}
      <RemovalRequestModal
        open={removalModalOpen}
        onClose={() => setRemovalModalOpen(false)}
        businessId={business?.id}
        businessName={business?.businessName}
        defaultType={removalDefaultType}
      />
    </div>
  );
};

export default BusinessDetail;
